import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'

/**
 * Base width cột cố định trên lưới list (px).
 * Checkbox / STT / trạng thái / ngày / thao tác — vừa đủ title + nội dung 1 hàng.
 * Cột thao tác: dùng `tableActionsColDims(iconCount)` (slot × số nút).
 */
export const TABLE_LEADING_WIDTHS = {
  /** Checkbox chọn dòng */
  checkbox: 30,
  /** STT / No. / 序号 */
  stt: 44,
} as const

/** Cột mã (SKU, mã khách, mã kho, …) — đủ rộng để hạn chế xuống nhiều dòng */
export const TABLE_COL_CODE = { minWidth: '200px', width: '224px' } as const

/** Cột checkbox — khóa width vừa khít. */
export const TABLE_COL_CHECKBOX = {
  minWidth: `${TABLE_LEADING_WIDTHS.checkbox}px`,
  width: `${TABLE_LEADING_WIDTHS.checkbox}px`,
  maxWidth: `${TABLE_LEADING_WIDTHS.checkbox}px`,
} as const

/** Cột STT — đủ rộng cho header (STT / No. / 序号) trên 1 hàng. */
export const TABLE_COL_STT = {
  minWidth: `${TABLE_LEADING_WIDTHS.stt}px`,
  width: `${TABLE_LEADING_WIDTHS.stt}px`,
  maxWidth: `${TABLE_LEADING_WIDTHS.stt}px`,
} as const

/** Mã ngắn / mã tham chiếu phụ */
export const TABLE_COL_CODE_M = { minWidth: '168px', width: '188px' } as const

/** Mã container / ref */
export const TABLE_COL_REF = { minWidth: '176px', width: '200px' } as const

/** Mã vận đơn / mã đơn dài */
export const TABLE_COL_LADING = { minWidth: '200px', width: '228px' } as const

/**
 * Cột ngày giờ `dd/MM/yyyy HH:mm` trên 1 hàng — khóa width vừa khít
 * (header «Ngày tạo» / «Ngày cập nhật»).
 * Meta list: dùng kèm `align: 'left'` (xem `tableListMetaCols`).
 */
export const TABLE_COL_DATETIME = {
  minWidth: '148px',
  width: '148px',
  maxWidth: '148px',
} as const

/**
 * Cột trạng thái chip `ActiveStatusChip` — khóa width vừa khít
 * (header «Trạng thái»; nhãn dài VD «Đang/Ngừng hoạt động»).
 * Meta list: dùng kèm `align: 'center'` (xem `tableListMetaCols`).
 */
export const TABLE_COL_ACTIVE_STATUS = {
  minWidth: '136px',
  width: '136px',
  maxWidth: '136px',
} as const

/**
 * Cột nút `TableListStatusToggleButton` (Bật / Tắt) — khóa width vừa khít.
 * Meta list Hàng hóa: dùng kèm `align: 'center'` (xem `tableListMetaCols`).
 */
export const TABLE_COL_ON_OFF_ACTION = {
  minWidth: '72px',
  width: '72px',
  maxWidth: '72px',
} as const

/** Dims cột leading (checkbox / STT) — width = minWidth = maxWidth. */
export function tableLeadingColDims(
  kind: keyof typeof TABLE_LEADING_WIDTHS,
): { width: string; minWidth: string; maxWidth: string } {
  const s = `${TABLE_LEADING_WIDTHS[kind]}px`
  return { width: s, minWidth: s, maxWidth: s }
}
export const tableListStatusToggleButtonSx = {
  minWidth: 0,
  textTransform: 'none' as const,
  height: 26,
  minHeight: 26,
  maxHeight: 26,
  py: 0,
  px: 1,
  borderRadius: `${banhangKiotLike.kiotFormInputRadiusPx}px`,
  fontFamily: banhangKiotLike.kiotFontStack,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.2,
  boxShadow: 'none',
  whiteSpace: 'nowrap' as const,
  '&:hover': { boxShadow: 'none' },
  '&:active': { boxShadow: 'none' },
  '& .MuiButton-startIcon': {
    marginRight: 0.25,
    marginLeft: -0.25,
    '& .MuiSvgIcon-root': { fontSize: 14 },
  },
} as const

/** Ghi đè ellipsis mặc định của TableList (selector cha specificity cao hơn sx ô). */
export const tableListCellAllowWrapSx = {
  whiteSpace: 'normal !important' as const,
  overflow: 'visible !important' as const,
  textOverflow: 'clip !important' as const,
  wordBreak: 'break-word' as const,
  verticalAlign: 'top' as const,
}

/** Cột nội dung nhiều dòng — gắn vào sx của TableCell tương ứng */
export const ORDER_LIST_WRAP_KEYS = new Set(['totalPack', 'fee', 'customer'])

export const SHIPMENT_LIST_WRAP_KEYS = new Set(['order', 'partner', 'warehouse', 'productName', 'images'])

export const PRODUCT_LIST_WRAP_KEYS = new Set(['name', 'note', 'description', 'businessNote'])

/** Khóa width cột trên body cell (tránh table-layout:fixed bung cột khi minWidth bảng lớn) */
export function tableListFixedColBodySx(col?: {
  width?: string
  minWidth?: string
  maxWidth?: string
}): {
  width?: string
  minWidth?: string
  maxWidth?: string
  boxSizing: 'border-box'
} {
  const w = col?.width ?? col?.minWidth
  if (!w) return { boxSizing: 'border-box' }
  return {
    width: w,
    minWidth: col?.minWidth ?? w,
    maxWidth: col?.maxWidth ?? w,
    boxSizing: 'border-box',
  }
}
